import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Sparkles,
  RefreshCw,
  Phone,
  PlayCircle,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbScriptTag } from "@/lib/breadcrumbSchema";


export const Route = createFileRoute("/are-we-a-fit")({
  head: () => ({
    meta: [
      {
        title: "Are We a Fit? | 2-Minute Quiz — Colorado Web Impressions",
      },
      {
        name: "description",
        content:
          "Take our 2-minute fit quiz. Honest answer on whether Colorado Web Impressions is right for your business — and exactly what to do if we&rsquo;re not.",
      },
      {
        property: "og:title",
        content: "Are we a fit? — 2-minute quiz",
      },
      {
        property: "og:description",
        content:
          "Honest fit-check before you book a call. We&rsquo;ll tell you straight if we&rsquo;re right or wrong for you.",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/are-we-a-fit" },
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/are-we-a-fit" }],
    scripts: [
      breadcrumbScriptTag([
        { name: "Home", path: "/" },
        { name: "Are We a Fit?" },
      ]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: "Are We a Fit? — Honest Answer from Colorado Web Impressions",
          description:
            "Chris Heidlebaugh gives a straight answer on who Colorado Web Impressions is — and isn't — the right fit for. Watch before you take the 2-minute quiz.",
          thumbnailUrl: "https://i.ytimg.com/vi/RqdSho8DCDM/maxresdefault.jpg",
          uploadDate: "2024-01-01",
          embedUrl: "https://www.youtube.com/embed/RqdSho8DCDM",
          contentUrl: "https://www.youtube.com/watch?v=RqdSho8DCDM",
          publisher: {
            "@type": "Organization",
            name: "Colorado Web Impressions",
            logo: {
              "@type": "ImageObject",
              url: "https://coloradowebimpressions.com/logo.png",
            },
          },
        }),
      },
    ],
  }),
  component: AreWeAFitPage,
});

type FitFlag = "outOfState" | "brandNew" | "lowBudget" | "urgent";

type Choice = {
  label: string;
  /** Each option carries weight: positive = strong fit signal, negative = needs the fast-lane plan */
  weight: number;
  /** Optional flag used to route to a tailored honest-answer outcome */
  flag?: FitFlag;
};

type Question = {
  id: string;
  q: string;
  help?: string;
  choices: Choice[];
};

const QUESTIONS: Question[] = [
  {
    id: "location",
    q: "Where is your business based?",
    choices: [
      { label: "Colorado — Front Range (Springs, Denver, Pueblo, etc.)", weight: 3 },
      { label: "Colorado — Northern, Western Slope, or Mountain", weight: 3 },
      { label: "Outside Colorado", weight: 0, flag: "outOfState" },
    ],
  },
  {
    id: "stage",
    q: "What stage is your business at?",
    choices: [
      { label: "Brand new — no revenue yet", weight: 0, flag: "brandNew" },
      { label: "Established — under $250K/yr", weight: 1 },
      { label: "Growing — $250K–$1M/yr", weight: 3 },
      { label: "Mature — $1M+/yr", weight: 3 },
    ],
  },
  {
    id: "type",
    q: "What kind of business do you run?",
    choices: [
      { label: "Home services (HVAC, plumbing, roofing, electrical, etc.)", weight: 3 },
      { label: "Trades / contractors / construction", weight: 3 },
      { label: "Local professional services (law, dental, medical, etc.)", weight: 2 },
      { label: "Restaurant, retail, or pure e-commerce", weight: 1 },
    ],
  },
  {
    id: "budget",
    q: "What can you realistically invest in marketing each month?",
    help: "This is total budget — including ad spend, your website, SEO, etc.",
    choices: [
      { label: "Under $500/month", weight: 0, flag: "lowBudget" },
      { label: "$500 – $1,500", weight: 1 },
      { label: "$1,500 – $5,000", weight: 3 },
      { label: "$5,000+", weight: 3 },
    ],
  },
  {
    id: "timeline",
    q: "When do you need leads coming in?",
    choices: [
      { label: "This week — I&rsquo;m desperate", weight: 1, flag: "urgent" },
      { label: "Within 30 days (paid ads territory)", weight: 2 },
      { label: "Within 3–6 months (SEO + ads sweet spot)", weight: 3 },
      { label: "Long-term play, 6+ months out", weight: 3 },
    ],
  },
  {
    id: "involvement",
    q: "How involved do you want to be?",
    choices: [
      { label: "I want to approve every word and pixel", weight: 1 },
      { label: "Quick check-ins are fine — I&rsquo;ll trust the experts", weight: 3 },
      { label: "Hands-off — just send me the report", weight: 2 },
    ],
  },
  {
    id: "values",
    q: "What matters most to you in an agency?",
    choices: [
      { label: "Cheapest possible price", weight: 0 },
      { label: "Transparent pricing &amp; no contracts", weight: 3 },
      { label: "Real, measurable results (calls + revenue)", weight: 3 },
      { label: "A long-term partnership", weight: 3 },
    ],
  },
];

type Outcome = {
  variant: "great" | "good" | "fast-lane" | "out-of-state";
  title: string;
  body: string;
  recommendations: string[];
  ctaLabel: string;
  ctaTo: "/contact" | "/honest-answers" | "/pricing";
};

function getOutcome(answers: Record<string, Choice>): Outcome {
  const flags = new Set<FitFlag>();
  for (const a of Object.values(answers)) {
    if (a.flag) flags.add(a.flag);
  }

  // Out-of-state: still welcome them, just be honest we're CO-focused.
  if (flags.has("outOfState")) {
    return {
      variant: "out-of-state",
      title: "We&rsquo;re Colorado-focused — but let&rsquo;s still talk",
      body:
        "We work almost exclusively with Colorado businesses so we can stay deeply local. That said, the playbook we&rsquo;d run for you (Google Ads + Google Business Profile + SEO + social) works in any market. Happy to share what we&rsquo;d do in your shoes — or send you to a great agency in your state.",
      recommendations: [
        "Get your Google Business Profile fully optimized — biggest free win in any local market.",
        "Run Google Ads now for fast leads while SEO compounds in the background.",
        "Want a second opinion? Get a proposal and we&rsquo;ll tell you straight what we&rsquo;d do.",
      ],
      ctaLabel: "Get a Proposal",
      ctaTo: "/contact",
    };
  }

  // Brand new, low budget, or "I need leads this week" → fast-lane plan, not rejection.
  if (flags.has("brandNew") || flags.has("lowBudget") || flags.has("urgent")) {
    const isBrandNew = flags.has("brandNew");
    const isLowBudget = flags.has("lowBudget");
    const isUrgent = flags.has("urgent");

    const recs: string[] = [];
    if (isUrgent || isBrandNew) {
      recs.push(
        "Start with Google Ads + an optimized Google Business Profile — these can produce calls in 2–4 weeks.",
      );
    }
    if (isBrandNew) {
      recs.push(
        "SEO takes 6–12 months to fully kick in for a brand-new site — but we start it day one so it compounds while ads carry the load.",
      );
      recs.push(
        "Layer in social media marketing to build trust and reviews while Google catches up.",
      );
    }
    if (isLowBudget) {
      recs.push(
        "At a tighter budget, focus your dollars on ONE channel that produces leads now (usually Ads or a lead-gen platform), then reinvest profits into SEO + a real website.",
      );
    }
    if (!isBrandNew && isUrgent) {
      recs.push(
        "We can have a paid-ads campaign live in about a week and SEO running in parallel for the long game.",
      );
    }
    recs.push(
      "Get a proposal — we&rsquo;ll lay out a realistic plan, timeline, and cost. No pressure.",
    );

    return {
      variant: "fast-lane",
      title: "Here&rsquo;s the honest path forward.",
      body:
        "Based on your answers, the smart play is a fast lane + long game: paid ads and Google Business Profile to get leads coming in now, with SEO and a strong website built up over the next 6–12 months. You absolutely can win — it just takes the right sequence.",
      recommendations: recs,
      ctaLabel: "Get a Proposal",
      ctaTo: "/contact",
    };
  }

  const score = Object.values(answers).reduce((s, a) => s + a.weight, 0);

  if (score >= 16) {
    return {
      variant: "great",
      title: "We&rsquo;re a strong fit. Let&rsquo;s talk.",
      body:
        "Based on your answers — Colorado business, healthy stage, real budget, realistic timeline, and the values match — we&rsquo;re built for businesses exactly like yours.",
      recommendations: [
        "Get a proposal. We&rsquo;ll audit your current marketing and lay out a clear plan.",
        "No pressure. If we&rsquo;re still not the right fit after the call, we&rsquo;ll tell you and refer you elsewhere.",
        "Bring your goals — leads/month, revenue target, and any current marketing spend.",
      ],
      ctaLabel: "Get a Proposal",
      ctaTo: "/contact",
    };
  }

  return {
    variant: "good",
    title: "Likely a good fit — worth a conversation.",
    body:
      "You&rsquo;re in our wheelhouse on most counts. A quick call lets us dig into the specifics and lay out a plan that actually fits your business.",
    recommendations: [
      "Get a proposal so we can dig into your business in detail.",
      "Read &ldquo;When we&rsquo;re NOT a fit&rdquo; beforehand so you can pressure-test our advice.",
      "Bring questions — we&rsquo;d rather lose a deal upfront than sign a bad-fit client.",
    ],
    ctaLabel: "Get a Proposal",
    ctaTo: "/contact",
  };
}

function AreWeAFitPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Choice>>({});
  const total = QUESTIONS.length;
  const isDone = step >= total;
  const progress = isDone ? 100 : Math.round((step / total) * 100);

  const outcome = useMemo(() => (isDone ? getOutcome(answers) : null), [isDone, answers]);

  const choose = (c: Choice) => {
    const q = QUESTIONS[step];
    setAnswers((prev) => ({ ...prev, [q.id]: c }));
    setStep((s) => s + 1);
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado Marketing Agency Fit Check"
        title={
          <>
            A 2-minute quiz. <span className="text-gradient-gold">Honest answer.</span>
          </>
        }
        description="No email required. No sales follow-up. We&rsquo;ll tell you straight whether we&rsquo;re a match — and if we&rsquo;re not, exactly what to do instead."
      >
        <div className="flex justify-center">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Are We a Fit?" }]} />
        </div>
      </PageHero>

      {/* Honest-answer intro video — compact */}
      <section className="bg-background pt-10 lg:pt-12">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="mb-4 flex items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-widest text-brand-gold-deep">
            <PlayCircle className="h-3.5 w-3.5" />
            Watch first · Honest answer from Chris
          </div>
          <div className="group relative overflow-hidden rounded-xl border border-border bg-card shadow-card transition-spring hover:border-brand-gold/40 hover:shadow-gold">
            <div className="absolute inset-x-0 top-0 z-10 h-0.5 bg-gradient-gold" />
            <div className="relative aspect-video w-full">
              <iframe
                src="https://www.youtube-nocookie.com/embed/RqdSho8DCDM?rel=0&modestbranding=1"
                title="Are We a Fit? — Honest Answer from Colorado Web Impressions"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-section-alt py-8 sm:py-10 lg:py-12">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          {/* Progress */}
          <div className="mb-6 flex items-center justify-between text-sm font-semibold text-muted-foreground">
            <span>
              {isDone ? "Done" : `Question ${step + 1} of ${total}`}
            </span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-border">
            <div
              className="h-full rounded-full bg-gradient-gold"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Quiz card */}
          {!isDone && (
            <div className="mt-8 rounded-2xl border border-border bg-card p-7 shadow-card sm:p-9">
              <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                {QUESTIONS[step].q}
              </h2>
              {QUESTIONS[step].help && (
                <p className="mt-2 text-sm text-muted-foreground">{QUESTIONS[step].help}</p>
              )}

              <div className="mt-6 space-y-3">
                {QUESTIONS[step].choices.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => choose(c)}
                    className="group flex w-full items-center justify-between gap-4 rounded-xl border border-border bg-background p-5 text-left text-base font-semibold text-foreground transition-smooth hover:-translate-y-0.5 hover:border-brand-gold hover:bg-brand-gold/5 hover:shadow-card"
                  >
                    <span dangerouslySetInnerHTML={{ __html: c.label }} />
                    <ArrowRight className="h-5 w-5 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                  </button>
                ))}
              </div>

              {step > 0 && (
                <button
                  type="button"
                  onClick={back}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-smooth hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              )}
            </div>
          )}

          {/* Result */}
          {isDone && outcome && <OutcomeCard outcome={outcome} onReset={reset} />}
        </div>
      </section>
    </SiteLayout>
  );
}

function OutcomeCard({ outcome, onReset }: { outcome: Outcome; onReset: () => void }) {
  // All outcomes route forward — every visitor gets a real plan + a CTA.
  const Icon = CheckCircle2;
  const accent = "text-brand-gold-deep";
  const variantLabel: Record<Outcome["variant"], string> = {
    great: "Strong Fit",
    good: "Likely Fit",
    "fast-lane": "Your Honest Plan",
    "out-of-state": "Colorado-Focused",
  };
  const isPositive = true;

  return (
    <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-card shadow-card-hover">
      <div className="flex items-center gap-4 bg-brand-gold/10 px-7 py-5 sm:px-9">
        <Icon className={`h-7 w-7 ${accent}`} />
        <span className={`text-xs font-bold uppercase tracking-[0.2em] ${accent}`}>
          {variantLabel[outcome.variant]}
        </span>
      </div>

      <div className="p-7 sm:p-9">
        <h2
          className="text-3xl font-extrabold text-foreground sm:text-4xl"
          dangerouslySetInnerHTML={{ __html: outcome.title }}
        />
        <p
          className="mt-4 text-base leading-relaxed text-foreground/90"
          dangerouslySetInnerHTML={{ __html: outcome.body }}
        />

        <h3 className="mt-7 text-sm font-bold uppercase tracking-wider text-brand-gold-deep">
          What we recommend
        </h3>
        <ul className="mt-3 space-y-3">
          {outcome.recommendations.map((r) => (
            <li key={r} className="flex items-start gap-3">
              <Sparkles className="mt-1 h-4 w-4 shrink-0 text-brand-gold-deep" />
              <span
                className="text-sm leading-relaxed text-foreground/90"
                dangerouslySetInnerHTML={{ __html: r }}
              />
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to={outcome.ctaTo}
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-3.5 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            {outcome.ctaLabel}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          {isPositive && (
            <a
              href="tel:+17194193935"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-7 py-3.5 text-base font-semibold text-foreground transition-smooth hover:border-brand-gold/50 hover:bg-muted"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          )}
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-7 py-3.5 text-base font-semibold text-foreground transition-smooth hover:border-brand-gold/50 hover:bg-muted"
          >
            <RefreshCw className="h-4 w-4" />
            Retake quiz
          </button>
        </div>
      </div>
    </div>
  );
}
